import { Inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpContextToken,
} from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BASE_PATH } from '../../core';
import { ErrorService } from '../services/errors/errorsService';

// HttpContext token to opt-out from showing error toast for specific requests
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  constructor(
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private errorService: ErrorService,
    @Inject(BASE_PATH) private apiBasePath: string
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        // Only handle HttpErrorResponse
        if (!(err instanceof HttpErrorResponse)) {
          return throwError(() => err);
        }

        // Respect opt-out on a per-request basis
        const skipToast = req.context.get(SKIP_ERROR_TOAST);
        if (skipToast) {
          return throwError(() => err);
        }

        // Limit to API requests (avoid assets/i18n and other external fetches)
        const isApiCall = typeof this.apiBasePath === 'string' && this.apiBasePath.length > 0
          ? req.url.startsWith(this.apiBasePath)
          : true; // if not configured, default to handle

        if (isApiCall && (err.status === 400 || err.status === 413)) {
          const message = this.buildErrorMessage(err);
          const closeLabel = this.translateKey('COMMON.CLOSE', 'Close');
          this.snackBar.open(message, closeLabel, {
            duration: 6000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar'],
          });
        }

        // Re-throw to preserve original behavior for subscribers
        return throwError(() => err);
      })
    );
  }

  private translateKey(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : fallback;
  }

  private buildErrorMessage(err: HttpErrorResponse): string {
    // Handle 413 Payload Too Large specifically
    if (err.status === 413) {
      return this.translateKey(
        'ERRORS.FILE_TOO_LARGE',
        'El archivo es demasiado grande. Por favor, reduce el tamaño del archivo e intenta de nuevo.'
      );
    }

    // Various shapes we may receive from backend
    const body = err.error;

    // PRIORIDAD: Códigos de negocio en errors[0].Code antes de cualquier otro campo (incluyendo body.message)
    if (Array.isArray(body?.errors) && body.errors.length > 0 && body.errors[0]?.Code) {
      try {
        // Use sync version to avoid async issues in interceptor
        // Error translations should be pre-loaded from previous errors
        const translatedBusiness = this.errorService.getTranslatedErrorSync(err);
        if (translatedBusiness) {
          return translatedBusiness;
        }
      } catch { /* ignore */ }
    }

    // 1) String body
    if (typeof body === 'string') {
      return body;
    }

    // 2) ProblemDetails-like (solo si no hubo código de negocio traducible)
    const title = body?.title;
    const detail = body?.detail || body?.message;
    if (title || detail) {
      return [title, detail].filter(Boolean).join(' - ');
    }

    // 3) ModelState-like { errors: { field: [msg1, msg2], ... } }
    const errorsObj = body?.errors;
    if (errorsObj && !Array.isArray(errorsObj) && typeof errorsObj === 'object') {
      const messages: string[] = [];
      for (const key of Object.keys(errorsObj)) {
        const val = errorsObj[key];
        if (Array.isArray(val)) {
          messages.push(...val.map(v => String(v)));
        } else if (val) {
          messages.push(String(val));
        }
      }
      if (messages.length) {
        return messages.slice(0, 5).join('\n');
      }
    }

    // 4) Generic fallback
    return this.translateKey('ERRORS.DEFAULT', 'Se ha producido un error de validación.');
  }
}
